import { API_BASE_PATH } from '../../apiConfig';
import type {
  AccountUser,
  GitHubAppConfigResponse,
  GitHubInstallationSummary,
  GitHubRepositoryBranchSummary,
  GitHubRepositoryDirectorySummary,
  GitHubRepositorySummary,
  Workspace,
  WorkspaceScanSummary
} from '../../components/screens/RunScreens';

interface RunStatusModel {
  model: string;
  aiAgent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  command?: string;
  exitCode?: number;
  timedOut?: boolean;
  issueCount?: number;
  submissionErrorCount?: number;
  error?: string;
}

interface RunLogEntry {
  time: string;
  message: string;
}

export interface RunStatusAgent {
  agent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  logFilePath?: string;
  logs?: RunLogEntry[];
  models: RunStatusModel[];
  error?: string;
}

interface RunRequestedAiAgentModel {
  aiAgent: string;
  model?: string;
}

export interface RunStatusDeduplication {
  status: string;
  finished: boolean;
  model?: string;
  judgeModel?: string;
  startedAt?: string;
  finishedAt?: string;
  outputPath?: string;
  issueCount?: number;
  deduplicatedIssueCount?: number;
  reportedIssueCount?: number;
  openAiRequestCount?: number;
  error?: string;
}

export interface RunStatusSnapshot {
  runId: string;
  target: string;
  requestedSkills: string[];
  requestedAiAgents: string[];
  requestedAiAgentModels?: RunRequestedAiAgentModel[];
  runDirectory: string;
  statusFilePath: string;
  branch?: string;
  scopePath?: string;
  scopePaths?: string[];
  preconditionsPath?: string;
  clonedRepositoryPath?: string;
  repositoryPath?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  agents: RunStatusAgent[];
  deduplication?: RunStatusDeduplication;
  report?: RunStatusDeduplication;
  setupError?: string;
}

interface RunIssueSubmission {
  Title: string;
  Summary: string;
  Detail: string;
  Impact: string;
  Files: string[];
  severity?: string;
}

export interface RunRawIssue {
  issueId: number;
  skill: string;
  scanAgent?: string;
  aiAgent?: string;
  relativePath: string;
  issue: RunIssueSubmission;
}

interface RunReportedIssue extends RunIssueSubmission {
  id: string;
  sourceIssueIds: number[];
  sources: RunRawIssue[];
  duplicateReasons: string[];
}

interface RunIssueReadError {
  relativePath: string;
  error: string;
}

export type RunIssueAcceptanceStatus = 'acknowledged' | 'accepted';

interface RunAcceptedIssue {
  id: string;
  status: RunIssueAcceptanceStatus;
  updatedAt: string;
}

interface RunIssuesPayload {
  allIssues?: RunRawIssue[];
  duplicatedIssues?: RunRawIssue[];
  deduplicatedIssues?: RunReportedIssue[];
  reportedIssues?: RunReportedIssue[];
  acceptedIssues?: RunAcceptedIssue[];
  readErrors?: RunIssueReadError[];
  deduplicationGeneratedAt?: string;
  reportGeneratedAt?: string;
  acceptedIssuesUpdatedAt?: string;
}

export interface RunDetailsResponse {
  status: RunStatusSnapshot;
  issues: RunIssuesPayload;
}

export interface RunIssueViewModel {
  id: string;
  title: string;
  summary: string;
  detail: string;
  impact: string;
  files: string[];
  sources: string[];
  severity?: string;
  acceptanceStatus?: RunIssueAcceptanceStatus;
  acceptanceUpdatedAt?: string;
}

export interface GitHubSessionResponse {
  sessionId: string;
  login?: string;
}

export interface GitHubInstallationsResponse {
  installations?: GitHubInstallationSummary[];
}

export interface GitHubRepositoriesResponse {
  repositories?: GitHubRepositorySummary[];
}

export interface GitHubBranchesResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
  };
  branches?: GitHubRepositoryBranchSummary[];
}

export interface GitHubRepositoryTreeResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
    branch?: string;
  };
  directories?: GitHubRepositoryDirectorySummary[];
}

const RUN_TERMINAL_STATUSES = ['completed', 'partial', 'failed'];
const RUN_REPORT_TERMINAL_STATUSES = ['completed', 'failed', 'skipped'];
const GITHUB_SESSION_STORAGE_KEY = 'scas-github-session-id';
const GITHUB_INSTALLATION_STORAGE_KEY = 'scas-github-installation-id';
const WORKSPACE_STORAGE_KEY = 'scas-current-workspace-id';
export const DEV_GITHUB_SESSION_ID = 'dev-github-session';
const DEV_GITHUB_INSTALLATION_ID = 99101;
const DEV_GITHUB_REPOSITORY_ID = 424201;
const DEV_ACCOUNT_USER_ID = 'dev-user-id';
const DEV_WORKSPACE_ID = 'dev-workspace-id';
const DEV_WORKSPACE_ID_SECONDARY = 'dev-workspace-id-secondary';

export const runIsDevGitHubAccountEnabled = (): boolean => {
  return process.env.NODE_ENV === 'development' && Boolean(process.env.REACT_APP_DEV_GITHUB_ACCOUNT?.trim());
};

export const runGetDevGitHubLogin = (): string => {
  const configuredLogin = process.env.REACT_APP_DEV_GITHUB_ACCOUNT?.trim() ?? '';

  return configuredLogin && !['1', 'true', 'yes'].includes(configuredLogin.toLowerCase())
    ? configuredLogin
    : 'havu-dev';
};

export const runGetDevGitHubInstallations = (): GitHubInstallationSummary[] => {
  const login = runGetDevGitHubLogin();

  return [{
    id: DEV_GITHUB_INSTALLATION_ID,
    account: { login, type: 'User' },
    targetType: 'User',
    repositorySelection: 'selected',
    htmlUrl: 'https://github.com/settings/installations/dev',
    appSlug: 'havu-dev'
  }];
};

export const runGetDevGitHubRepositories = (): GitHubRepositorySummary[] => {
  const login = runGetDevGitHubLogin();
  const permissions = { admin: true, maintain: true, push: true, pull: true };

  return [
    {
      id: DEV_GITHUB_REPOSITORY_ID,
      owner: login,
      repo: 'marketplace-api',
      fullName: `${login}/marketplace-api`,
      private: true,
      htmlUrl: `https://github.com/${login}/marketplace-api`,
      defaultBranch: 'main',
      permissions
    },
    {
      id: DEV_GITHUB_REPOSITORY_ID + 1,
      owner: login,
      repo: 'payments-contracts',
      fullName: `${login}/payments-contracts`,
      private: true,
      htmlUrl: `https://github.com/${login}/payments-contracts`,
      defaultBranch: 'audit-readiness',
      permissions
    }
  ];
};

export const DEV_GITHUB_BRANCHES: GitHubRepositoryBranchSummary[] = [
  { name: 'main', default: true, protected: true, commitSha: '8f4a27c' },
  { name: 'audit-readiness', default: false, protected: false, commitSha: 'd26bc91' },
  { name: 'feature/checkout-hardening', default: false, protected: false, commitSha: '51a9e20' }
];

export const DEV_GITHUB_TREE_DIRECTORIES: GitHubRepositoryDirectorySummary[] = [
  { path: 'api', name: 'api', depth: 0 },
  { path: 'api/controllers', name: 'controllers', depth: 1 },
  { path: 'api/middleware', name: 'middleware', depth: 1 },
  { path: 'api/routes', name: 'routes', depth: 1 },
  { path: 'api/services', name: 'services', depth: 1 },
  { path: 'api/services/billing', name: 'billing', depth: 2 },
  { path: 'api/services/github', name: 'github', depth: 2 },
  { path: 'contracts', name: 'contracts', depth: 0 },
  { path: 'contracts/src', name: 'src', depth: 1 },
  { path: 'contracts/test', name: 'test', depth: 1 },
  { path: 'frontend', name: 'frontend', depth: 0 },
  { path: 'frontend/src', name: 'src', depth: 1 },
  { path: 'frontend/src/components', name: 'components', depth: 2 },
  { path: 'frontend/src/pages', name: 'pages', depth: 2 },
  { path: 'infra', name: 'infra', depth: 0 },
  { path: 'docs', name: 'docs', depth: 0 }
];

export const runGetDevAccountUser = (): AccountUser => {
  const login = runGetDevGitHubLogin();

  return {
    id: DEV_ACCOUNT_USER_ID,
    githubLogin: login,
    githubAvatarUrl: `https://avatars.githubusercontent.com/${login}`,
    email: `${login}@example.com`
  };
};

export const runGetDevWorkspaces = (): Workspace[] => {
  const now = new Date().toISOString();

  return [
    {
      id: DEV_WORKSPACE_ID,
      ownerUserId: DEV_ACCOUNT_USER_ID,
      name: 'Acme Protocol',
      slug: 'acme-protocol',
      createdAt: now,
      updatedAt: now
    },
    {
      id: DEV_WORKSPACE_ID_SECONDARY,
      ownerUserId: DEV_ACCOUNT_USER_ID,
      name: 'Sandbox',
      slug: 'sandbox',
      createdAt: now,
      updatedAt: now
    }
  ];
};

export const runGetDevWorkspaceScans = (workspaceId: string): WorkspaceScanSummary[] => {
  if (workspaceId !== DEV_WORKSPACE_ID) return [];

  const hoursAgo = (hours: number): string => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

  return [
    {
      id: 'dev-scan-running', workspaceId, createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'web-api', target: 'Run 3: f02e1', status: 'running', sourceType: 'github',
      branch: 'feature/checkout-hardening', createdAt: hoursAgo(0.25), updatedAt: hoursAgo(0.1)
    },
    {
      id: 'dev-scan-partial', workspaceId, createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'smart-contract', target: 'Run 2: 88b1d', status: 'partial', sourceType: 'github',
      branch: 'audit-readiness', createdAt: hoursAgo(4), updatedAt: hoursAgo(3), completedAt: hoursAgo(3)
    },
    {
      id: 'dev-scan-completed', workspaceId, createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'smart-contract', target: 'Run 1: a3f9c', status: 'completed', sourceType: 'github',
      branch: 'main', createdAt: hoursAgo(26), updatedAt: hoursAgo(25), completedAt: hoursAgo(25)
    }
  ];
};

export const DEV_FULL_SKILLS = [
  'semgrep-code-security',
  'trail-of-bits-static-analysis',
  'trail-of-bits-supply-chain',
  'quillshield',
  'forefy'
];

const runFormatErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };
    if (typeof error === 'string' && error.trim()) return error;
  }

  return `Request failed with status ${fallbackStatus}`;
};

export const runRequestJson = async <ResponseType,>(path: string, init: RequestInit = {}): Promise<ResponseType> => {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${API_BASE_PATH}${path}`, { ...init, headers, credentials: 'include' });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (!response.ok) throw new Error(runFormatErrorMessage(payload, response.status));
  return payload as ResponseType;
};

const runGetResultProcessingStatus = (status: RunStatusSnapshot | undefined): RunStatusDeduplication | undefined => {
  return status?.deduplication ?? status?.report;
};

const runGetStoredGitHubSessionId = (): string => window.localStorage.getItem(GITHUB_SESSION_STORAGE_KEY) ?? '';

export const runStoreGitHubSessionId = (sessionId: string): void => {
  if (sessionId === DEV_GITHUB_SESSION_ID) return;
  if (sessionId) window.localStorage.setItem(GITHUB_SESSION_STORAGE_KEY, sessionId);
  else window.localStorage.removeItem(GITHUB_SESSION_STORAGE_KEY);
};

export const runGetInitialGitHubSessionId = (): string => {
  if (runIsDevGitHubAccountEnabled()) return DEV_GITHUB_SESSION_ID;
  return new URLSearchParams(window.location.search).get('github_session') ?? runGetStoredGitHubSessionId();
};

export const runClearStoredAccountState = (): void => {
  window.localStorage.removeItem(GITHUB_SESSION_STORAGE_KEY);
  window.localStorage.removeItem(GITHUB_INSTALLATION_STORAGE_KEY);
  window.localStorage.removeItem(WORKSPACE_STORAGE_KEY);
};

export const runGetStoredWorkspaceId = (): string => window.localStorage.getItem(WORKSPACE_STORAGE_KEY) ?? '';

export const runStoreWorkspaceId = (workspaceId: string): void => {
  if (workspaceId) window.localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
  else window.localStorage.removeItem(WORKSPACE_STORAGE_KEY);
};

export const runClearGitHubCallbackParams = (): void => {
  const params = new URLSearchParams(window.location.search);
  const callbackParams = ['github_session', 'github_connected', 'github_error', 'installation_id', 'setup_action', 'state'];
  if (!callbackParams.some((param) => params.has(param))) return;

  callbackParams.forEach((param) => params.delete(param));
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
};

export const runIsReady = (details: RunDetailsResponse | null): boolean => {
  if (!details) return false;
  const resultProcessing = runGetResultProcessingStatus(details.status);

  return RUN_TERMINAL_STATUSES.includes(details.status.status)
    && Boolean(resultProcessing)
    && RUN_REPORT_TERMINAL_STATUSES.includes(resultProcessing?.status ?? '');
};

export const runGetIssues = (details: RunDetailsResponse | null): RunIssueViewModel[] => {
  if (!details?.issues) return [];

  const acceptedById = new Map((details.issues.acceptedIssues ?? []).map((issue) => [issue.id, issue]));
  const reportedIssues = details.issues.deduplicatedIssues ?? details.issues.reportedIssues ?? [];

  if (reportedIssues.length > 0) {
    return reportedIssues.map((issue) => {
      const acceptedIssue = acceptedById.get(issue.id);

      return {
        id: issue.id,
        title: issue.Title,
        summary: issue.Summary,
        detail: issue.Detail,
        impact: issue.Impact,
        files: issue.Files,
        severity: issue.severity,
        acceptanceStatus: acceptedIssue?.status,
        acceptanceUpdatedAt: acceptedIssue?.updatedAt,
        sources: issue.sources.map((source) => {
          const scanAgent = source.scanAgent ? ` / ${source.scanAgent}` : '';
          const aiAgent = source.aiAgent ? ` / ${source.aiAgent}` : '';
          return `${source.skill}${scanAgent}${aiAgent} #${source.issueId}`;
        })
      };
    });
  }

  return (details.issues.allIssues ?? []).map((entry) => {
    const id = String(entry.issueId);
    const acceptedIssue = acceptedById.get(id);

    return {
      id,
      title: entry.issue.Title,
      summary: entry.issue.Summary,
      detail: entry.issue.Detail,
      impact: entry.issue.Impact,
      files: entry.issue.Files,
      severity: entry.issue.severity,
      acceptanceStatus: acceptedIssue?.status,
      acceptanceUpdatedAt: acceptedIssue?.updatedAt,
      sources: [`${entry.skill}${entry.scanAgent ? ` / ${entry.scanAgent}` : ''}${entry.aiAgent ? ` / ${entry.aiAgent}` : ''}`]
    };
  });
};

export type { GitHubAppConfigResponse };
